import { useEffect, useState } from 'react'
import { fxCustomersApi } from '../../services/api'
import ExportBar, { ExportColumn } from '../../components/ExportBar'
import TableActionButton from '../../components/TableActionButton'
import LocationPicker from '../../components/LocationPicker'
import DistrictPicker from '../../components/DistrictPicker'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'

interface Customer {
  id: number
  name: string
  phone: string | null
  address: string | null
  district: string | null
  latitude: number | null
  longitude: number | null
  dni: string | null
  ruc: string | null
  notes: string | null
  is_active: boolean
  created_at: string
}

interface CustomerForm {
  name: string
  phone: string
  address: string
  district: string
  latitude: number | null
  longitude: number | null
  dni: string
  ruc: string
  notes: string
}

const emptyForm: CustomerForm = {
  name: '', phone: '', address: '', district: '', latitude: null, longitude: null, dni: '', ruc: '', notes: '',
}

export default function FxCustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<CustomerForm>(emptyForm)
  const [showLocationPicker, setShowLocationPicker] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const fetchCustomers = async () => {
    try {
      const res = await fxCustomersApi.list()
      setCustomers(res.data.customers || [])
    } catch (err) {
      console.error('Error fetching FX customers:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCustomers()
  }, [])

  const { sortedData, sortConfig, requestSort } = useSortableData(customers, {
    key: 'name',
    direction: 'asc',
  })

  const exportColumns: ExportColumn<Customer>[] = [
    { key: 'name', label: 'Nombre' },
    { key: 'phone', label: 'Teléfono', format: (c) => c.phone || '-' },
    { key: 'district', label: 'Distrito', format: (c) => c.district || '-' },
    { key: 'address', label: 'Dirección', format: (c) => c.address || '-' },
    { key: 'dni', label: 'DNI', format: (c) => c.dni || '-' },
    { key: 'ruc', label: 'RUC', format: (c) => c.ruc || '-' },
    { key: 'created_at', label: 'Añadido', format: (c) => new Date(c.created_at).toLocaleDateString() },
  ]

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleDistrictChange = (value: string) => setForm((prev) => ({ ...prev, district: value }))

  const openNew = () => {
    setForm(emptyForm)
    setEditingId(null)
    setError('')
    setShowLocationPicker(false)
    setShowForm(true)
  }

  const openEdit = (c: Customer) => {
    setForm({
      name: c.name,
      phone: c.phone || '',
      address: c.address || '',
      district: c.district || '',
      latitude: c.latitude ?? null,
      longitude: c.longitude ?? null,
      dni: c.dni || '',
      ruc: c.ruc || '',
      notes: c.notes || '',
    })
    setEditingId(c.id)
    setError('')
    setShowLocationPicker(c.latitude != null && c.longitude != null)
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('El nombre es requerido')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = {
        ...form,
        phone: form.phone || null,
        address: form.address || null,
        district: form.district || null,
        dni: form.dni || null,
        ruc: form.ruc || null,
        notes: form.notes || null,
      }
      if (editingId) {
        await fxCustomersApi.update(editingId, payload)
      } else {
        await fxCustomersApi.create(payload)
      }
      setShowForm(false)
      fetchCustomers()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar cliente')
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (c: Customer) => {
    try {
      // Same toggle-via-update pattern as SuppliersPage/ProductsPage, not the
      // DELETE endpoint -- that only sets is_active=false and has no inverse,
      // so it can't reactivate. list_fx_customers no longer filters by
      // is_active, so a deactivated row stays visible for this toggle to act on.
      await fxCustomersApi.update(c.id, { is_active: !c.is_active })
      fetchCustomers()
    } catch (err) {
      console.error('Error updating FX customer status:', err)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-espresso-800">Clientes — FX Admin</h1>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium"
        >
          + Agregar Cliente
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingId ? 'Editar Cliente' : 'Nuevo Cliente'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Nombre *</label>
                <input
                  type="text" name="name" value={form.name} onChange={handleChange} required
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Teléfono</label>
                  <input
                    type="text" name="phone" value={form.phone} onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">DNI</label>
                  <input
                    type="text" name="dni" value={form.dni} onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">RUC</label>
                  <input
                    type="text" name="ruc" value={form.ruc} onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
                <DistrictPicker value={form.district} onChange={handleDistrictChange} />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Dirección</label>
                <textarea
                  name="address" value={form.address} onChange={handleChange} rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div>
                {showLocationPicker ? (
                  <>
                    <label className="block text-sm font-medium text-espresso-700 mb-1">Ubicación en el mapa</label>
                    <LocationPicker
                      latitude={form.latitude}
                      longitude={form.longitude}
                      onChange={(lat, lng) => setForm((prev) => ({ ...prev, latitude: lat, longitude: lng }))}
                    />
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowLocationPicker(true)}
                    className="text-sm text-terracotta-700 hover:text-terracotta-800 underline"
                  >
                    + Agregar ubicación en el mapa (opcional)
                  </button>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Notas</label>
                <textarea
                  name="notes" value={form.notes} onChange={handleChange} rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50">
                  {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {customers.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay clientes aún. Agrega el primero para comenzar.</p>
        </div>
      ) : (
        <>
          <ExportBar data={customers} columns={exportColumns} filename="fx_clientes" title="Clientes FX Admin" />
          <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-espresso-100">
                <thead className="bg-cream-50">
                  <tr>
                    <SortableTh label="Nombre" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Teléfono" sortKey="phone" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Distrito" sortKey="district" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="DNI" sortKey="dni" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="RUC" sortKey="ruc" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Añadido" sortKey="created_at" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Estado" sortKey="is_active" sortConfig={sortConfig} onSort={requestSort} />
                    <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-cream-50 divide-y divide-espresso-100">
                  {sortedData.map((c) => (
                    <tr key={c.id} className="hover:bg-cream-100">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{c.name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{c.phone || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{c.district || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{c.dni || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{c.ruc || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-500">
                        {new Date(c.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${c.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {c.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end gap-1">
                          <TableActionButton onClick={() => openEdit(c)} variant="primary">Editar</TableActionButton>
                          <TableActionButton onClick={() => toggleActive(c)} variant={c.is_active ? 'danger' : 'primary'}>
                            {c.is_active ? 'Desactivar' : 'Activar'}
                          </TableActionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
